import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';
import { Snackbar, Alert } from '@mui/material';
import { handleApiError } from '../utils/errorHandler';

interface ErrorContextType {
  showError: (error: any) => void;
  showCustomError: (message: string) => void;
}

const ErrorContext = createContext<ErrorContextType | undefined>(undefined);

export const ErrorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [error, setError] = useState<string | null>(null);

  const showError = useCallback((error: any) => {
    const errorMessage = handleApiError(error);
    setError(errorMessage);
  }, []);

  const showCustomError = useCallback((message: string) => {
    setError(message);
  }, []);

  const handleClose = useCallback(() => {
    setError(null);
  }, []);

  const errorContextValue = useMemo(
    () => ({ showError, showCustomError }),
    [showError, showCustomError],
  );

  return (
    <ErrorContext.Provider value={errorContextValue}>
      {children}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          mt: 2,
          '& .MuiAlert-root': {
            backgroundColor: '#FFF2F2',
            color: '#D92D20',
            fontFamily: "dana, 'Arial'",
            fontSize: '0.95rem',
            padding: '16px 20px',
            borderRadius: '12px',
            border: '1px solid #FEE4E2',
            boxShadow: '0 4px 8px rgba(217, 45, 32, 0.05)',
            minWidth: '320px',
            maxWidth: '600px',
            alignItems: 'center',
            overflowY: 'visible',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          },
          '& .MuiAlert-icon': {
            color: '#D92D20',
            opacity: 1,
            marginRight: '12px',
          },
          '& .MuiAlert-message': {
            padding: 0,
            fontWeight: 500,
            overflow: 'visible'
          },
          '& .MuiAlert-action': {
            padding: 0,
            marginRight: 0,
          },
        }}
      >
        <Alert 
          onClose={handleClose} 
          severity="error" 
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </ErrorContext.Provider>
  );
};

export const useError = () => {
  const context = useContext(ErrorContext);
  if (context === undefined) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}; 